import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { 
  Calendar,
  MessageSquare,
  CheckCircle,
  AlertTriangle,
  Clock,
  User,
  Phone,
  Mail,
  FileText,
  Activity,
  Bell,
  ChevronRight,
  Filter,
  Search,
  MoreHorizontal,
  type LucideProps
} from 'lucide-react';

// --- TYPE DEFINITIONS ---

const activityTypes = [
  'appointment', 'message', 'task', 'alert', 'reminder', 'call', 'email', 'document', 'system', 'notification', 'default'
] as const;

const priorityLevels = ['high', 'medium', 'low', 'normal'] as const;

type ActivityType = typeof activityTypes[number];
type Priority = typeof priorityLevels[number];

export interface ActivityAction {
  id: string;
  label: string;
  icon?: React.ElementType;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive';
  disabled?: boolean;
}

export interface ActivityUser {
  name?: string;
  avatar?: string;
  initials?: string;
  role?: string;
}

export interface Activity {
  id: string;
  type?: ActivityType;
  title: string;
  description?: string;
  timestamp?: string;
  priority?: Priority;
  user?: ActivityUser;
  actions?: ActivityAction[];
}

export interface Tab {
  id: string;
  label: string;
  icon?: React.ElementType;
  count?: number;
  filterFn?: (activity: Activity) => boolean;
}

// --- CONSTANTS ---

export const ACTIVITY_ICONS: Record<ActivityType, React.ComponentType<LucideProps>> = {
  appointment: Calendar,
  message: MessageSquare,
  task: CheckCircle,
  alert: AlertTriangle,
  reminder: Clock,
  call: Phone,
  email: Mail,
  document: FileText,
  system: Activity,
  notification: Bell,
  default: Activity,
};

export const ACTIVITY_COLORS: Record<ActivityType, { bg: string; text: string; border: string; icon: string; }> = {
  appointment: { bg: 'bg-sky-100', text: 'text-sky-800', border: 'border-sky-200', icon: 'text-sky-600' },
  message: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-200', icon: 'text-emerald-600' },
  task: { bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-200', icon: 'text-violet-600' },
  alert: { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-200', icon: 'text-rose-600' },
  reminder: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200', icon: 'text-orange-600' },
  call: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', icon: 'text-blue-600' },
  email: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-200', icon: 'text-indigo-600' },
  document: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200', icon: 'text-gray-600' },
  system: { bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-200', icon: 'text-slate-600' },
  notification: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200', icon: 'text-yellow-600' },
  default: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200', icon: 'text-gray-600' },
};

export const PRIORITY_COLORS: Record<Priority, { bg: string; text: string; border: string; }> = {
  high: { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-200' },
  medium: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
  low: { bg: 'bg-sky-100', text: 'text-sky-800', border: 'border-sky-200' },
  normal: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' },
};

const DEFAULT_ACTIVITIES_TO_SHOW = 10;

// --- SUB-COMPONENTS ---

interface ActivityItemProps {
  activity: Activity;
  onActivityClick?: (activity: Activity) => void;
  onActionClick?: (action: ActivityAction, activity: Activity) => void;
  className?: string;
}

const ActivityItem = React.forwardRef<HTMLDivElement, ActivityItemProps>((
  { activity, onActivityClick, onActionClick, className = '' }, ref) => {
  
  const { type = 'default', title, description, timestamp, user, actions, priority = 'normal' } = activity;
  const theme = ACTIVITY_COLORS[type] || ACTIVITY_COLORS.default;
  const priorityTheme = PRIORITY_COLORS[priority] || PRIORITY_COLORS.normal;
  const IconComponent = ACTIVITY_ICONS[type] || ACTIVITY_ICONS.default;

  const handleActivityClick = () => onActivityClick?.(activity);
  const handleActionClick = (action: ActivityAction, e: React.MouseEvent) => {
    e.stopPropagation();
    onActionClick?.(action, activity);
  };

  return (
    <div
      ref={ref}
      className={cn(
        'group flex w-full cursor-pointer items-start gap-4 p-4 transition-colors duration-200',
        'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
        'border-b border-gray-200',
        className
      )}
      onClick={handleActivityClick}
      tabIndex={0}
      role="button"
      aria-label={`Activity: ${title}`}
    >
      <Avatar className={cn('h-10 w-10', theme.icon)}>
        <AvatarImage src={user?.avatar} alt={user?.name} />
        <AvatarFallback className={cn(theme.bg, theme.text)}>{user?.initials || <User />}</AvatarFallback>
      </Avatar>

      <div className="flex-1 space-y-1.5">
        <div className="flex items-center justify-between">
          <p className="font-semibold text-gray-900">{title}</p>
          {timestamp && <p className="text-xs text-gray-500">{timestamp}</p>}
        </div>
        {description && <p className="text-sm text-gray-600">{description}</p>}
        <div className="flex items-center gap-2 pt-1">
          <Badge variant="outline" className={cn(priorityTheme.bg, priorityTheme.text, priorityTheme.border)}>{priority}</Badge>
          {actions && actions.map((action) => (
            <Button
              key={action.id}
              variant={action.variant || 'secondary'}
              size="sm"
              onClick={(e) => handleActionClick(action, e)}
              disabled={action.disabled}
              className="h-7 px-2 py-1 text-xs"
            >
              {action.icon && <action.icon className="mr-1.5 h-3.5 w-3.5" />}
              {action.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
});
ActivityItem.displayName = 'ActivityItem';

// --- MAIN COMPONENT ---

export interface ActivityHubProps {
  title?: string;
  activities?: Activity[];
  tabs?: Tab[];
  activeTab?: string;
  showSearch?: boolean;
  showTabs?: boolean;
  maxItems?: number;
  showLoadMore?: boolean;
  onActivityClick?: (activity: Activity) => void;
  onActionClick?: (action: ActivityAction, activity: Activity) => void;
  onTabChange?: (tabId: string) => void;
  onSearch?: (query: string) => void;
  onLoadMore?: () => void;
  /**
   * When true, the activity list will scroll internally with a max height.
   * Default is false so the card grows with its content and parent manages scroll.
   */
  scrollable?: boolean;
  /**
   * Tailwind class to control the max height when scrollable is true.
   * Provide utility classes like `max-h-96 sm:max-h-[28rem]`.
   */
  maxHeightClass?: string;
  /**
   * Mobile-first responsive scroll behavior: scroll on small screens,
   * show full list (no scroll) on sm and up.
   */
  responsiveScroll?: boolean;
  className?: string;
}

const ActivityHub = React.forwardRef<HTMLDivElement, ActivityHubProps>((
  {
    title = 'Activity Hub',
    activities = [],
    tabs = [],
    activeTab: controlledActiveTab,
    showSearch = false,
    showTabs = true,
    maxItems = DEFAULT_ACTIVITIES_TO_SHOW,
    showLoadMore = false,
    scrollable = false,
    maxHeightClass = 'max-h-96 sm:max-h-[28rem]',
    responsiveScroll = false,
    onActivityClick,
    onActionClick,
    onTabChange,
    onSearch,
    onLoadMore,
    className = '',
  }, ref) => {

  const [internalActiveTab, setInternalActiveTab] = useState(tabs[0]?.id || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleItemCount, setVisibleItemCount] = useState(maxItems);

  const activeTab = controlledActiveTab ?? internalActiveTab;

  const handleTabClick = (tabId: string) => {
    if (!controlledActiveTab) {
      setInternalActiveTab(tabId);
    }
    onTabChange?.(tabId);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    onSearch?.(query);
  };

  const handleLoadMore = () => {
    const newCount = visibleItemCount + maxItems;
    setVisibleItemCount(newCount);
    onLoadMore?.();
  };

  const activeTabFilter = tabs.find(t => t.id === activeTab)?.filterFn;

  const filteredActivities = activities
    .filter(activity => {
      if (!searchQuery) return true;
      const lowerCaseQuery = searchQuery.toLowerCase();
      return (
        activity.title.toLowerCase().includes(lowerCaseQuery) ||
        activity.description?.toLowerCase().includes(lowerCaseQuery) ||
        activity.user?.name?.toLowerCase().includes(lowerCaseQuery)
      );
    })
    .filter(activity => activeTabFilter ? activeTabFilter(activity) : true);

  const visibleActivities = filteredActivities.slice(0, visibleItemCount);

  return (
    <Card ref={ref} className={cn('overflow-hidden', className)}>
      <CardHeader className="flex h-16 shrink-0 flex-row items-center justify-between border-b px-4">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {showSearch && (
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input placeholder="Search activities..." className="h-9 pl-9" value={searchQuery} onChange={handleSearchChange} />
          </div>
        )}
      </CardHeader>

      <CardContent className="p-0">
        {showTabs && tabs.length > 0 && (
          <nav className="flex shrink-0 border-b px-4 overflow-x-auto whitespace-nowrap">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2.5 text-sm font-medium',
                  activeTab === tab.id
                    ? 'border-b-2 border-indigo-600 text-indigo-600'
                    : 'text-gray-600 hover:text-gray-800'
                )}
                aria-current={activeTab === tab.id ? 'page' : undefined}
              >
                {tab.icon && <tab.icon className="h-4 w-4" />}
                <span>{tab.label}</span>
                {tab.count != null && (
                  <Badge
                    variant={activeTab === tab.id ? 'default' : 'secondary'}
                    className="ml-1 inline-flex h-5 items-center justify-center px-1.5 text-xs"
                  >
                    {tab.count}
                  </Badge>
                )}
              </button>
            ))}
          </nav>
        )}

        <div
          className={cn(
            responsiveScroll
              ? 'max-h-96 overflow-y-auto sm:max-h-none sm:overflow-visible'
              : [scrollable && 'overflow-y-auto', scrollable && maxHeightClass]
          )}
        >
          {visibleActivities.length > 0 ? (
            visibleActivities.map(activity => (
              <ActivityItem 
                key={activity.id} 
                activity={activity} 
                onActivityClick={onActivityClick} 
                onActionClick={onActionClick} 
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <Bell className="h-12 w-12 text-gray-300" />
              <p className="mt-4 font-medium text-gray-700">No Activities</p>
              <p className="mt-1 text-sm text-gray-500">There are no activities to display right now.</p>
            </div>
          )}
        </div>
      </CardContent>

      {showLoadMore && visibleActivities.length < filteredActivities.length && (
        <CardFooter className="flex items-center justify-center border-t p-2">
          <Button variant="link" onClick={handleLoadMore}>Load More</Button>
        </CardFooter>
      )}
    </Card>
  );
});

ActivityHub.displayName = 'ActivityHub';

export default ActivityHub;
